import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logError } from "../../common/utils/logger";
import { formatCurrency } from "../../common/utils/formatters";
import { scrollToTopOnMount } from "../../common/utils/scrollToTop";
import { useJourney } from "../../common/hooks/useJourney";
import { getLeadDateOfBirth } from "../../common/utils/journeyLeadFields";
import { submitFormData } from "../../api/formService";
import { JOURNEY_STAGE, PAYMENT_FLOW, PAYMENT_TYPE_BIZ_CODE } from "../../api/journeyService";
import {
  downloadTofConsentForm,
  fetchTofConsentPdfBlob,
} from "../../api/paymentService";
import { uploadDocument } from "../../api/documentService";
import {
  sendTransferOfFundsOtp,
  verifyTransferOfFundsOtp,
} from "../../api/transferOfFundsOtpService";
import { normalizeJourneyStages } from "../../common/utils/journeyResume";
import { ERROR_MESSAGES, JOURNEY_PLAN_NAMES, JOURNEY_TYPES } from "../../constants";
import { journeyActions } from "../../store/journeySlice";
import NeedhelpModal from "../../common/components/NeedhelpModal";
import Modal from "../../common/components/Modal";
import PaymentModal from "../../common/components/PaymentModal";
import "./Summary.scss";
// OTP popup styles (.otp-modal-overlay etc.) are shared with Proposal Review.
import "../proposal/Proposal.scss";
import "./TransferOfFunds.scss";

const TOF_DOC_CATEGORY = "KYC";
const TOF_DOC_TYPE_CODE = "Fund_Transfer_Letter";
const TOF_DOC_TYPE = "PR_OtherDocs";

const formatConsentDateDdMmYyyy = (value = new Date()) => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const isConsentUploadedFlag = (value) =>
  value === true ||
  value === "true" ||
  String(value || "").toLowerCase() === "y" ||
  String(value || "").toLowerCase() === "yes";

const RESEND_SECONDS = 30;

// Short titles are display headings only; `text` is the full legal wording.
const TOF_TERMS = [
  {
    title: "Transfer shortfall",
    text: "If the amount available from the existing policy is insufficient, I will be required to pay the remaining premium to issue the new policy or to keep another policy active.",
  },
  {
    title: "Excess funds",
    text: "If there is any excess amount remaining after the transfer, it will be credited to my registered bank account.",
  },
  {
    title: "Receipt of proceeds",
    text: "The new policy will be issued only after successful receipt of proceeds from the existing policy.",
  },
  {
    title: "Policy issuance",
    text: "The new policy will be issued upon submission and fulfilment of all required documents and requirements. Final decision would be subject to underwriting.",
  },
  {
    title: "ULIP reinvestment value",
    text: "For ULIP policies, the reinvestment amount will be considered based on the fund value on the date of form signing; however, the final reinvestment amount will depend on the fund value on the date of maturity or foreclosure of the existing policy.",
  },
  {
    title: "ULIP valuation & NAV",
    text: "The new ULIP policy shall be issued based on the Net Asset Value (NAV) applicable on the date of issuance.",
  },
  {
    title: "ULIP surrender cut-off",
    text: "In case of Surrender of ULIP policies, the surrender value will be determined based on the same-day NAV, provided the surrender request is received before 3:00 PM on a NAV day. For surrender requests received after 3:00 PM, the NAV of the next NAV day will be applicable.",
  },
];

const getTofDeclarations = ({ mobile, date }) => [
  {
    key: "authorizedPerson",
    text: "I confirm that I am the policyholder/authorized person entitled to request transfer of funds from the source policy and that the details furnished by me are true and correct.",
  },
  {
    key: "otpConsent",
    text: mobile
      ? `I hereby provide my consent through OTP sent to my registered mobile number ${mobile} on ${date} and authorize IndusInd Nippon Life Insurance Company Limited to transfer the eligible amount available under the stated policy to the new application mentioned above, subject to applicable terms and conditions, deductions, taxes and regulatory requirements.`
      : `I hereby provide my consent through OTP sent to the mobile number registered against the source policy on ${date} and authorize IndusInd Nippon Life Insurance Company Limited to transfer the eligible amount available under the stated policy to the new application mentioned above, subject to applicable terms and conditions, deductions, taxes and regulatory requirements.`,
  },
  {
    key: "termsAccepted",
    text: "I confirm that I have read, understood and accepted the terms and conditions governing this transfer request.",
  },
  {
    key: "otpAuthorization",
    text: "The successful validation of the OTP shall be deemed as my authorization and acceptance of this transaction.",
  },
];

const formatToday = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const maskMobileSuffix = (suffix) => {
  if (!suffix) return "";
  return `+91 *** **${String(suffix).slice(-4)}`;
};

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const parsed =
    typeof data === "string"
      ? (() => {
          try {
            return JSON.parse(data);
          } catch {
            return null;
          }
        })()
      : data;
  return (
    parsed?.errorMessage ||
    parsed?.error ||
    parsed?.message ||
    (typeof data === "string" ? data : null) ||
    fallback
  );
};

/**
 * Transfer of Funds (ToF) request page, opened from Summary's
 * "Pay via Fund Transfer". The server looks up the source policy and sends the
 * OTP to the mobile on that policy. The browser never chooses the number.
 */
const TransferOfFunds = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    applicationNumber: journeyApplicationNumber,
    quoteResult,
    journeyData,
  } = useJourney();
  const leadFormData = useSelector((s) => s.journey.leadFormData);
  const {
    applicationNumber: stateApplicationNumber,
    customerName: stateCustomerName,
    premiumAmount: statePremiumAmount,
    planName: statePlanName,
    dateOfBirth: stateDateOfBirth,
  } = location.state || {};
  const customerName =
    stateCustomerName ||
    leadFormData?.proposerName ||
    leadFormData?.fullName ||
    "";
  const premiumAmount =
    statePremiumAmount || quoteResult?.totalPayableAmount || "";
  /** Transfer amount cap — the total payable amount (input accepts whole rupees). */
  const maxTransferAmount =
    Math.round(Number(String(premiumAmount).replace(/[^\d.]/g, ""))) || 0;
  const applicationNumber = stateApplicationNumber || journeyApplicationNumber;
  const planName =
    statePlanName ||
    quoteResult?.productName ||
    JOURNEY_PLAN_NAMES[JOURNEY_TYPES.NISCHIT_PENSION];
  const dateOfBirth =
    stateDateOfBirth || getLeadDateOfBirth(leadFormData, null, quoteResult);

  const [sourcePolicyNumber, setSourcePolicyNumber] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  /** Values sent with the OTP / submitLead — shown on the success popup and PDF. */
  const [submittedRequest, setSubmittedRequest] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [acceptedDeclarations, setAcceptedDeclarations] = useState({});
  const [error, setError] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [consentDownloading, setConsentDownloading] = useState(false);
  const [continuingToKyc, setContinuingToKyc] = useState(false);
  const [continueError, setContinueError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  /** OTP + timestamp the customer consented with — printed on the consent PDF. */
  const verifiedConsentRef = useRef(null);
  const consentAlreadyUploadedRef = useRef(false);
  /** Once the user edits the amount, stop overwriting it with prefill/resume values. */
  const transferAmountEditedRef = useRef(false);
  /** Last 4 digits returned by the server after it sends the OTP. The full number stays on the server. */
  const [mobileSuffix, setMobileSuffix] = useState("");
  const [showPolicyLookupModal, setShowPolicyLookupModal] = useState(false);
  const [policyLookupMessage, setPolicyLookupMessage] = useState("");
  const sourcePolicyRef = useRef("");
  const otpRequestSeq = useRef(0);

  useEffect(() => {
    scrollToTopOnMount();
  }, []);

  useEffect(() => {
    sourcePolicyRef.current = sourcePolicyNumber.trim();
  }, [sourcePolicyNumber]);

  // Pre-fill the transfer amount with the total payable amount (still editable).
  useEffect(() => {
    if (transferAmountEditedRef.current || !maxTransferAmount) return;
    setTransferAmount((current) => current || String(maxTransferAmount));
  }, [maxTransferAmount]);

  useEffect(() => {
    const transfer = normalizeJourneyStages(journeyData)?.[
      JOURNEY_STAGE.TRANSFER_OF_FUNDS
    ];
    if (!transfer || typeof transfer !== "object") return;
    if (transfer.tofPolicyNumber) {
      setSourcePolicyNumber((current) => current || String(transfer.tofPolicyNumber));
    }
    // A saved (resumed) amount takes precedence over the total-payable prefill.
    if (transfer.tofTransactionAmount && !transferAmountEditedRef.current) {
      setTransferAmount(String(transfer.tofTransactionAmount));
    }
    if (transfer.tofPolicyNumber && transfer.tofTransactionAmount) {
      setSubmittedRequest((current) =>
        current || {
          sourcePolicyNumber: String(transfer.tofPolicyNumber),
          transferAmount: Number(transfer.tofTransactionAmount),
        },
      );
    }
    consentAlreadyUploadedRef.current = isConsentUploadedFlag(
      transfer.tofConsentDocumentUploaded,
    );
  }, [journeyData]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const timeout = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
    return () => clearTimeout(timeout);
  }, [resendTimer]);

  // const handleBack = () => navigate("/summary");
  const handleBack = () => navigate("/summary", { state: { fromTransferOfFunds: true } });

  const declarations = getTofDeclarations({
    mobile: maskMobileSuffix(mobileSuffix),
    date: formatToday(),
  });
  const allDeclarationsAccepted = declarations.every(
    (d) => acceptedDeclarations[d.key],
  );
  const getTransferAmountError = (amount) => {
    if (!amount || Number(amount) <= 0) {
      return "Please enter a valid transfer amount.";
    }
    if (maxTransferAmount && Number(amount) > maxTransferAmount) {
      return `Maximum payable amount is ${formatCurrency(maxTransferAmount)}`;
    }
    return "";
  };
  const canSubmit =
    !!sourcePolicyNumber.trim() &&
    !!applicationNumber &&
    !getTransferAmountError(transferAmount) &&
    allDeclarationsAccepted;

  const validateForm = () => {
    const errors = {};
    if (!sourcePolicyNumber.trim()) {
      errors.sourcePolicyNumber = "Please enter the source policy number.";
    }
    if (!applicationNumber) {
      errors.applicationNumber = "Application number is not available.";
    }
    const amountError = getTransferAmountError(transferAmount);
    if (amountError) {
      errors.transferAmount = amountError;
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const showLookupPopup = (message) => {
    setPolicyLookupMessage(message);
    setShowPolicyLookupModal(true);
  };

  /**
   * Server looks up the source policy and sends the OTP to its mobile.
   * Returns null when the OTP modal can open, or a message when it cannot.
   */
  const requestPolicyOtp = async (policyNumber) => {
    const seq = ++otpRequestSeq.current;
    try {
      const response = await sendTransferOfFundsOtp({
        policyNumber,
        applicationNumber,
        dateOfBirth,
        customerName,
      });
      if (sourcePolicyRef.current !== policyNumber || seq !== otpRequestSeq.current) {
        return "";
      }
      if (response?.status !== "success" || !response?.mobileSuffix) {
        setMobileSuffix("");
        showLookupPopup(ERROR_MESSAGES.TOF_POLICY_NOT_FOUND);
        return ERROR_MESSAGES.TOF_POLICY_NOT_FOUND;
      }
      setMobileSuffix(String(response.mobileSuffix));
      setShowPolicyLookupModal(false);
      setOtpValue("");
      setResendTimer(RESEND_SECONDS);
      return null;
    } catch (err) {
      if (sourcePolicyRef.current !== policyNumber || seq !== otpRequestSeq.current) {
        return "";
      }
      logError("[TransferOfFunds] policy OTP request failed", err);
      const message = getApiErrorMessage(err, ERROR_MESSAGES.TOF_POLICY_LOOKUP_FAILED);
      setMobileSuffix("");
      const popupMessage =
        message === ERROR_MESSAGES.TOF_POLICY_NOT_FOUND
          ? ERROR_MESSAGES.TOF_POLICY_NOT_FOUND
          : ERROR_MESSAGES.TOF_POLICY_LOOKUP_FAILED;
      showLookupPopup(popupMessage);
      return popupMessage;
    }
  };

  const buildConsentPdfParams = (consentOverride = null) => {
    const consent = consentOverride || verifiedConsentRef.current;
    return {
      applicationNumber,
      dateOfBirth,
      otp: consent?.otp || "",
      consentDate: consent?.verifiedAt
        ? formatConsentDateDdMmYyyy(consent.verifiedAt)
        : formatConsentDateDdMmYyyy(),
    };
  };

  /** Saves the ToF request via submitLead (applicationNumber + DOB encrypted in submitFormData). */
  const submitTofLead = async (request, transactionStatus, consentMeta = null) => {
    try {
      await submitFormData({
        applicationNumber,
        dateOfBirth,
        productLob: JOURNEY_TYPES.NISCHIT_PENSION,
        planName,
        currentStage: JOURNEY_STAGE.TRANSFER_OF_FUNDS,
        tofPolicyNumber: request.sourcePolicyNumber,
        tofTransactionAmount: request.transferAmount,
        tofRequestedAt: new Date().toISOString(),
        paymentTypeBizCode: PAYMENT_TYPE_BIZ_CODE.INTERNAL_FUND_TRANSFER,
        ...(transactionStatus ? { transactionStatus } : {}),
        ...(consentMeta || {}),
        tofDeclarations: declarations.map((d) => ({
          key: d.key,
          accepted: !!acceptedDeclarations[d.key],
        })),
      });
      return null;
    } catch (err) {
      logError("[TransferOfFunds] submitLead failed", err);
      return getApiErrorMessage(
        err,
        "Unable to submit your request. Please try again.",
      );
    }
  };

  const uploadConsentPdfToBizOps = async (blob, filename) => {
    const formData = new FormData();
    formData.append("applicationNumber", applicationNumber);
    if (dateOfBirth) {
      formData.append("dateOfBirth", dateOfBirth);
    }
    formData.append("documentCategory", TOF_DOC_CATEGORY);
    formData.append("documentTypeCode", TOF_DOC_TYPE_CODE);
    formData.append("documentType", TOF_DOC_TYPE);
    formData.append("docFormat", "PDF");
    formData.append("docPageSideFB", "M");
    formData.append("addedBy", "system");
    formData.append("sourceFrom", "online");
    formData.append(
      "file",
      blob,
      filename || `FundTransferLetter_${applicationNumber}.pdf`,
    );
    await uploadDocument(formData);
  };

  const handleSubmit = async () => {
    if (sendingOtp || !canSubmit || !validateForm()) return;
    setError("");
    // Snapshot what was submitted — the popup, PDF and submitLead use this,
    // not the live inputs (browser autofill can touch them once the OTP
    // password field appears).
    const request = {
      sourcePolicyNumber: sourcePolicyNumber.trim(),
      transferAmount: Number(transferAmount),
    };
    setSubmittedRequest(request);
    setSendingOtp(true);
    try {
      const sendError = await requestPolicyOtp(request.sourcePolicyNumber);
      if (sendError !== null) return;
      const submitError = await submitTofLead(request);
      if (submitError) {
        setError(submitError);
        return;
      }
      setOtpError("");
      setShowOtpModal(true);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0 || sendingOtp) return;
    const policyNumber = sourcePolicyRef.current.trim();
    if (!policyNumber) return;
    setSendingOtp(true);
    setOtpError((await requestPolicyOtp(policyNumber)) || "");
    setSendingOtp(false);
  };

  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) return;
    const request = submittedRequest || {
      sourcePolicyNumber: sourcePolicyNumber.trim(),
      transferAmount: Number(transferAmount),
    };
    try {
      setVerifyingOtp(true);
      setOtpError("");
      const response = await verifyTransferOfFundsOtp({
        policyNumber: request.sourcePolicyNumber,
        applicationNumber,
        dateOfBirth,
        otp: otpValue,
      });
      if (response?.status !== "success") {
        setOtpError(
          response?.message ||
            "OTP verification failed. Please re-enter the OTP and try again.",
        );
        await markTransferFailed();
        return;
      }

      const consent = { otp: otpValue, verifiedAt: new Date() };
      verifiedConsentRef.current = consent;

      // Upload Fund Transfer Letter on OTP success (not on Continue).
      if (!consentAlreadyUploadedRef.current) {
        const { blob, filename } = await fetchTofConsentPdfBlob(
          buildConsentPdfParams(consent),
        );
        await uploadConsentPdfToBizOps(blob, filename);
        consentAlreadyUploadedRef.current = true;
      }

      const submitError = await submitTofLead(request, "SUCCESS", {
        tofConsentDocumentUploaded: true,
        tofConsentUploadedAt: new Date().toISOString(),
      });
      if (submitError) {
        setOtpError(submitError);
        return;
      }

      dispatch(journeyActions.setPaymentTransactionStatus("SUCCESS"));
      setShowOtpModal(false);
      setOtpValue("");
      setShowSuccessModal(true);
    } catch (err) {
      logError("[TransferOfFunds] OTP verification / consent upload failed", err);
      setOtpError(
        getApiErrorMessage(
          err,
          "OTP verification failed. Please re-enter the OTP and try again.",
        ),
      );
      // Only mark FAILED when OTP itself failed; upload errors keep PENDING for retry.
      if (!verifiedConsentRef.current) {
        await markTransferFailed();
      }
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleDownloadConsent = async () => {
    if (consentDownloading) return;
    setConsentError("");
    setConsentDownloading(true);
    try {
      await downloadTofConsentForm(buildConsentPdfParams());
    } catch (err) {
      logError("[TransferOfFunds] consent PDF download failed", err);
      setConsentError(
        getApiErrorMessage(
          err,
          "Unable to download consent form. Please try again.",
        ),
      );
    } finally {
      setConsentDownloading(false);
    }
  };

  const markTransferFailed = async () => {
    const request = submittedRequest || {
      sourcePolicyNumber: sourcePolicyNumber.trim(),
      transferAmount: Number(transferAmount),
    };
    if (!request.sourcePolicyNumber || !request.transferAmount) return;
    await submitTofLead(request, "FAILED");
  };

  const handleContinueToKyc = () => {
    if (continuingToKyc || !verifiedConsentRef.current) return;
    setContinuingToKyc(true);
    setContinueError("");
    try {
      dispatch(journeyActions.setPaymentFlowMode(PAYMENT_FLOW.PAY_FIRST));
      dispatch(journeyActions.setStage("customerVerification"));
      navigate("/verification");
    } finally {
      setContinuingToKyc(false);
    }
  };

  return (
    <div className="insurance-journey-summary transfer-of-funds">
      <header className="sumarry-header">
        <div className="sumarry-header-wraper">
          <div className="header-left">
            <button
              className="back-button"
              onClick={handleBack}
              aria-label="Go back"
            >
              <svg
                width="18"
                height="14"
                viewBox="0 0 18 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17 7H1M1 7L7 13M1 7L7 1"
                  stroke="#252527"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h6 className="header-title m-0">Transfer of Funds Request</h6>
          </div>
          <button
            className="help-button btn secondary-button regular"
            onClick={() => setShowHelpModal(true)}
          >
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.6772 2.87806C15.3151 1.50799 13.5769 0.573101 11.683 0.191867C9.789 -0.189367 7.82449 0.000214264 6.03838 0.736587C4.25227 1.47296 2.72496 2.72298 1.64998 4.32823C0.575006 5.93349 0.000755732 7.82174 0 9.75369V15.0037C0 15.6004 0.237053 16.1727 0.65901 16.5947C1.08097 17.0166 1.65326 17.2537 2.25 17.2537H3.75C4.34674 17.2537 4.91903 17.0166 5.34099 16.5947C5.76295 16.1727 6 15.6004 6 15.0037V11.2537C6 10.657 5.76295 10.0847 5.34099 9.6627C4.91903 9.24074 4.34674 9.00369 3.75 9.00369H1.53375C1.67839 7.43988 2.26578 5.94969 3.22709 4.70779C4.18839 3.46588 5.48377 2.52372 6.96141 1.99172C8.43904 1.45972 10.0377 1.35993 11.57 1.70404C13.1024 2.04815 14.5048 2.82191 15.6131 3.93463C16.9677 5.29616 17.8009 7.09031 17.9672 9.00369H15.75C15.1533 9.00369 14.581 9.24074 14.159 9.6627C13.7371 10.0847 13.5 10.657 13.5 11.2537V15.0037C13.5 15.6004 13.7371 16.1727 14.159 16.5947C14.581 17.0166 15.1533 17.2537 15.75 17.2537H18C18 17.8504 17.7629 18.4227 17.341 18.8447C16.919 19.2666 16.3467 19.5037 15.75 19.5037H10.5C10.3011 19.5037 10.1103 19.5827 9.96967 19.7234C9.82902 19.864 9.75 20.0548 9.75 20.2537C9.75 20.4526 9.82902 20.6434 9.96967 20.784C10.1103 20.9247 10.3011 21.0037 10.5 21.0037H15.75C16.7446 21.0037 17.6984 20.6086 18.4017 19.9053C19.1049 19.2021 19.5 18.2483 19.5 17.2537V9.75369C19.5049 8.47829 19.2581 7.21446 18.7737 6.03462C18.2893 4.85477 17.5768 3.7821 16.6772 2.87806ZM3.75 10.5037C3.94891 10.5037 4.13968 10.5827 4.28033 10.7234C4.42098 10.864 4.5 11.0548 4.5 11.2537V15.0037C4.5 15.2026 4.42098 15.3934 4.28033 15.534C4.13968 15.6747 3.94891 15.7537 3.75 15.7537H2.25C2.05109 15.7537 1.86032 15.6747 1.71967 15.534C1.57902 15.3934 1.5 15.2026 1.5 15.0037V10.5037H3.75ZM15.75 15.7537C15.5511 15.7537 15.3603 15.6747 15.2197 15.534C15.079 15.3934 15 15.2026 15 15.0037V11.2537C15 11.0548 15.079 10.864 15.2197 10.7234C15.3603 10.5827 15.5511 10.5037 15.75 10.5037H18V15.7537H15.75Z"
                fill="#97262A"
              />
            </svg>
            Need Help?
          </button>
        </div>
      </header>

      <div className="summary-content">
        <div className="summary-wraper">
          <div className="summary-left-panel tof-panel">
            <div className="tof-page">
              <div className="tof-hero">
                <div className="tof-hero-head">
                  <span className="tof-hero-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4L3 8L7 12M3 8H17M17 12L21 16L17 20M21 16H7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <div>
                    <h6 className="tof-hero-title m-0">Pay via Fund Transfer</h6>
                    <p className="tof-hero-subtitle m-0">
                      Transfer funds from your existing policy
                    </p>
                  </div>
                </div>
                <div className="tof-application-pill hide">
                  Application Number: {applicationNumber}
                </div>
              </div>

              <div className="tof-card border-0 bg-white">
                <div className="tof-amount-row">
                  <div>
                    <p className="tof-muted m-0">Requested transfer amount</p>
                    <p className="tof-amount m-0">
                      {premiumAmount ? formatCurrency(premiumAmount).replace("₹ ", "₹") : "--"}
                    </p>
                  </div>
                  <span className="tof-icon-badge tof-icon-badge-lg" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 7V5.5A1.5 1.5 0 0 0 17.5 4H5.5A2.5 2.5 0 0 0 3 6.5v11A2.5 2.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15M3 6.5A2.5 2.5 0 0 0 5.5 9H19.5A1.5 1.5 0 0 1 21 10.5V15h-4a2 2 0 0 1 0-4h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>

                <div className="tof-divider" />

                <div className="tof-form-grid">
                  <div className="form-field">
                    <label className="tof-label" htmlFor="tof-source-policy">
                      Source policy number <span className="tof-required">*</span>
                    </label>
                    <input
                      id="tof-source-policy"
                      type="text"
                      autoComplete="off"
                      className="form-input"
                      placeholder="Enter policy number"
                      maxLength={10}
                      value={sourcePolicyNumber}
                      onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, "");
                        sourcePolicyRef.current = next;
                        if (next !== sourcePolicyNumber) {
                          otpRequestSeq.current += 1;
                          setMobileSuffix("");
                          setShowOtpModal(false);
                          setOtpValue("");
                        }
                        setSourcePolicyNumber(next);
                        if (formErrors.sourcePolicyNumber) {
                          setFormErrors((prev) => ({ ...prev, sourcePolicyNumber: "" }));
                        }
                      }}
                    />
                    {formErrors.sourcePolicyNumber && (
                      <p className="tof-field-error">{formErrors.sourcePolicyNumber}</p>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="tof-label" htmlFor="tof-target-application">
                      Target application
                    </label>
                    <input
                      id="tof-target-application"
                      type="text"
                      className="form-input"
                      value={applicationNumber || ""}
                      readOnly
                      disabled
                    />
                    {formErrors.applicationNumber && (
                      <p className="tof-field-error">{formErrors.applicationNumber}</p>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="tof-label" htmlFor="tof-amount">
                      Transfer amount (₹) <span className="tof-required">*</span>
                    </label>
                    <input
                      id="tof-amount"
                      type="text"
                      autoComplete="off"
                      inputMode="numeric"
                      className="form-input"
                      placeholder="Enter transfer amount"
                      value={
                        transferAmount
                          ? Number(transferAmount).toLocaleString("en-IN")
                          : ""
                      }
                      onChange={(e) => {
                        const amount = e.target.value.replace(/\D/g, "").slice(0, 10);
                        transferAmountEditedRef.current = true;
                        setTransferAmount(amount);
                        // Show the max-limit error as soon as it is exceeded.
                        const exceedsMax =
                          maxTransferAmount && Number(amount) > maxTransferAmount;
                        setFormErrors((prev) => ({
                          ...prev,
                          transferAmount: exceedsMax ? getTransferAmountError(amount) : "",
                        }));
                      }}
                    />
                    {formErrors.transferAmount && (
                      <p className="tof-field-error">{formErrors.transferAmount}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="tof-card border-0 bg-white">
                <div
                  className="tof-card-head"
                  role="button"
                  tabIndex={0}
                  aria-expanded={showTerms}
                  aria-controls="tof-terms-list"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowTerms((prev) => !prev)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowTerms((prev) => !prev);
                    }
                  }}
                >
                  <span className="tof-icon-badge" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2ZM9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                  <h6 className="tof-card-title m-0">Terms &amp; Conditions</h6>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    style={{
                      marginLeft: "auto",
                      transition: "transform 0.2s ease",
                      transform: showTerms ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  >
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <ol id="tof-terms-list" className="tof-terms-list" hidden={!showTerms}>
                  {TOF_TERMS.map((term, index) => (
                    <li key={term.title} className="tof-term align-items-center">
                      <span className="tof-term-index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div>
                        {/* <p className="tof-term-title m-0">{term.title}</p> */}
                        <p className="tof-term-text m-0">{term.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <p className="tof-footnote m-0">
                  Refer to the complete applicable terms for detailed conditions and cut-off timings.
                </p>
              </div>

              <div className="tof-card border-0 bg-white">
                <div className="tof-card-head tof-card-head-top">
                  <span className="tof-icon-badge" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <div>
                    <h6 className="tof-card-title m-0">Customer Declaration</h6>
                    <p className="tof-muted m-0">Please confirm all statements to continue.</p>
                  </div>
                </div>
                <div className="tof-declarations">
                  {declarations.map((d) => (
                    <label key={d.key} className="declaration-item">
                      <input
                        type="checkbox"
                        checked={!!acceptedDeclarations[d.key]}
                        onChange={(e) => {
                          const { checked } = e.target;
                          setAcceptedDeclarations((prev) => ({
                            ...prev,
                            [d.key]: checked,
                          }));
                        }}
                      />
                      <span className="declaration-text">{d.text}</span>
                    </label>
                  ))}
                </div>

                <div className="tof-divider" />

                <div className="tof-otp-note">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <rect x="6" y="2.5" width="12" height="19" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M11 18h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  <div>
                    <p className="tof-otp-title m-0">OTP verification</p>
                    <p className="tof-footnote m-0">
                      {mobileSuffix
                        ? `OTP will be sent to the mobile number linked to this policy, ending in ${mobileSuffix}`
                        : "OTP will be sent to your registered mobile number linked to the source policy."}
                    </p>
                  </div>
                </div>

                {error && (
                  <div
                    className="error-message error-message-inline"
                    role="alert"
                    aria-live="assertive"
                  >
                    {error}
                  </div>
                )}
                <button
                  type="button"
                  className="tof-submit-btn btn primary-button regular"
                  onClick={handleSubmit}
                  disabled={sendingOtp || !canSubmit}
                >
                  {sendingOtp ? "Sending OTP..." : "Proceed to OTP verification →"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showOtpModal && (
        <div className="otp-modal-overlay">
          <div className="otp-modal">
            <div className="otp-header">
              <h5 className="otp-modal-title light">OTP Verification</h5>
            </div>
            <button
              className="otp-modal-close"
              onClick={() => setShowOtpModal(false)}
              aria-label="Close"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.781 13.7198C14.8507 13.7895 14.906 13.8722 14.9437 13.9632C14.9814 14.0543 15.0008 14.1519 15.0008 14.2504C15.0008 14.349 14.9814 14.4465 14.9437 14.5376C14.906 14.6286 14.8507 14.7114 14.781 14.781C14.7114 14.8507 14.6286 14.906 14.5376 14.9437C14.4465 14.9814 14.349 15.0008 14.2504 15.0008C14.1519 15.0008 14.0543 14.9814 13.9632 14.9437C13.8722 14.906 13.7895 14.8507 13.7198 14.781L7.50042 8.56073L1.28104 14.781C1.14031 14.9218 0.94944 15.0008 0.750417 15.0008C0.551394 15.0008 0.360523 14.9218 0.219792 14.781C0.0790615 14.6403 3.92322e-09 14.4494 0 14.2504C-3.92322e-09 14.0514 0.0790615 13.8605 0.219792 13.7198L6.4401 7.50042L0.219792 1.28104C0.0790615 1.14031 0 0.94944 0 0.750417C0 0.551394 0.0790615 0.360523 0.219792 0.219792C0.360523 0.0790615 0.551394 0 0.750417 0C0.94944 0 1.14031 0.0790615 1.28104 0.219792L7.50042 6.4401L13.7198 0.219792C13.8605 0.0790615 14.0514 -3.92322e-09 14.2504 0C14.4494 3.92322e-09 14.6403 0.0790615 14.781 0.219792C14.9218 0.360523 15.0008 0.551394 15.0008 0.750417C15.0008 0.94944 14.9218 1.14031 14.781 1.28104L8.56073 7.50042L14.781 13.7198Z"
                  fill="#3C3D3D"
                />
              </svg>
            </button>
            <div className="otp-body">
              <h6 className="large-body semibold">Enter OTP Code</h6>

              <p className="small-body regular">
                We've sent a 6-digit OTP to your mobile number
              </p>

              <div className="otp-phone">
                <p>
                  <span>Phone Number:</span>{" "}
                  <strong>{maskMobileSuffix(mobileSuffix)}</strong>
                </p>
              </div>

              <div className="form-field">
                <label className="label-one">Enter 6 digit OTP</label>
                <input
                  type="password"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="X X X X X X"
                  className="otp-input"
                  value={otpValue}
                  onChange={(e) => {
                    setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6));
                    if (otpError) {
                      setOtpError("");
                    }
                  }}
                />

                {otpError && <p className="pivc-error">{otpError}</p>}

                <span
                  className={`otp-resend ${resendTimer > 0 ? "disabled" : ""}`}
                  onClick={handleResendOtp}
                >
                  {resendTimer > 0
                    ? `Resend OTP in ${resendTimer}s`
                    : "Resend OTP"}
                </span>
              </div>
              <div className="otp-footer">
                <button
                  className="otp-continue-btn btn primary-button regular"
                  disabled={verifyingOtp || otpValue.length !== 6}
                  onClick={handleVerifyOtp}
                >
                  {verifyingOtp ? "Verifying..." : "Continue"}
                </button>
                <p className="otp-secure">100% Secure. No Spam Guarantee</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={showPolicyLookupModal}
        onClose={() => setShowPolicyLookupModal(false)}
        title={
          policyLookupMessage === ERROR_MESSAGES.TOF_POLICY_NOT_FOUND
            ? "Policy Not Found"
            : "Error"
        }
        type="error"
        btnTitle="Close"
      >
        <p>{policyLookupMessage}</p>
      </Modal>
      <NeedhelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Reach us through your preferred mode"
        type="success"
      />
      <PaymentModal
        isOpen={showSuccessModal}
        title="Fund Transfer Request Submitted"
        type="success"
      >
        <div className="payment-modal-body">
          <p className="small-body regular">
           Your request to transfer{" "}
            <span className="small-body semibold">
              {formatCurrency(submittedRequest?.transferAmount)}
            </span>{" "}
            from policy{" "}
            <span className="small-body semibold">
              {(submittedRequest?.sourcePolicyNumber)}
            </span>{" "}
            towards Application No.{" "}
            <span className="small-body semibold">{applicationNumber}</span>{" "}
            has been received.
          </p>
          <p className="small-body regular">
          You can now continue with your application. Your new policy will be issued after the funds are received from your existing policy, and once all requirements are met.
          </p>

          <div className="paymet-modal-btns">
            <button
              type="button"
              className={`modal-btn btn secondary-button regular ${consentDownloading ? "wait" : "pointer"}`}
              onClick={handleDownloadConsent}
              disabled={consentDownloading}
            >
              {consentDownloading ? "Downloading..." : "Download Consent Form"}
            </button>
            <button
              type="button"
              className="modal-btn btn primary-button regular"
              onClick={handleContinueToKyc}
              disabled={continuingToKyc}
            >
              {continuingToKyc ? "Continuing..." : "Continue"}
            </button>
            {continueError || consentError ? (
              <span className="payment-receipt-error-text">{continueError || consentError}</span>
            ) : null}
          </div>
        </div>
      </PaymentModal>
    </div>
  );
};

export default TransferOfFunds;
